import { GAME_CONFIG } from '../config';
import { HeadPos, MissZone, Trajectory, TrajectoryConfig } from '../types';
import { getFloorY } from '../utils/coordinates';

export class TrajectorySystem {
  static makeConfigFromGameConfig(): TrajectoryConfig {
    return {
      idealForce: GAME_CONFIG.IDEAL_FORCE,
      tolerance: GAME_CONFIG.FORCE_TOLERANCE,
      lateralSensitivity: GAME_CONFIG.LATERAL_SENSITIVITY,
      flightDuration: GAME_CONFIG.FLIGHT_DURATION,
      vanishingX: GAME_CONFIG.VANISHING_X,
      floorYAtZMinus1: GAME_CONFIG.FLOOR_Y_AT_Z_MINUS_1,
      floorYAtZZero: GAME_CONFIG.FLOOR_Y_AT_Z_ZERO,
      floorYAtZPlus1: GAME_CONFIG.FLOOR_Y_AT_Z_PLUS_1,
      hitWindowMult: 1.0,
    };
  }

  static calculate(
    rawVx: number,
    rawVy: number,
    startScreenX: number,
    startScreenY: number,
    headPos: HeadPos,
    config: TrajectoryConfig,
  ): Trajectory {
    const verticalForce = -rawVy;
    const forceRatio = verticalForce / config.idealForce;

    const xOffset = (rawVx / 800) * 80 * config.lateralSensitivity;
    const lateralOff = xOffset;
    // Required lateral compensation for character's current position. Stage 1
    // (static character) gives 0; later stages shift the perfect zone toward
    // wherever the character is dancing right now.
    const targetLateralOff = headPos.x - GAME_CONFIG.CHAR_CENTER_X;
    const lateralAbs = Math.abs(lateralOff - targetLateralOff);
    const forceDelta = Math.abs(forceRatio - 1);

    const mult = config.hitWindowMult;
    const tolPerfect = config.tolerance * GAME_CONFIG.HIT_FORCE_PERFECT_MULT * mult;
    const tolGood = config.tolerance * GAME_CONFIG.HIT_FORCE_GOOD_MULT * mult;
    const tolJust = config.tolerance * GAME_CONFIG.HIT_FORCE_JUST_MULT * mult;

    const latPerfect = GAME_CONFIG.HIT_LATERAL_PERFECT * mult;
    const latGood = GAME_CONFIG.HIT_LATERAL_GOOD * mult;
    const latJust = GAME_CONFIG.HIT_LATERAL_JUST * mult;

    const inPerfect = lateralAbs <= latPerfect && forceDelta <= tolPerfect;
    const inGood = lateralAbs <= latGood && forceDelta <= tolGood;
    const inJust = lateralAbs <= latJust && forceDelta <= tolJust;

    const rotationSpeed =
      (Math.abs(rawVx) / GAME_CONFIG.HAT_ROT_VELOCITY_DIVISOR + GAME_CONFIG.HAT_ROT_BASE_SPEED) *
      Math.sign(rawVx || 1);

    if (inPerfect || inGood || inJust) {
      const hitType: 'perfect' | 'good' | 'just' = inPerfect ? 'perfect' : inGood ? 'good' : 'just';
      return TrajectorySystem.buildHitTrajectory(
        startScreenX,
        startScreenY,
        xOffset,
        headPos,
        forceRatio,
        config,
        rotationSpeed,
        hitType,
      );
    }

    let zone: MissZone;
    const lateralRelative = lateralOff - targetLateralOff;
    if (forceRatio < 1 - tolJust) {
      zone = 'front';
    } else if (forceRatio > 1 + tolJust) {
      zone = 'back';
    } else if (lateralRelative < -latJust) {
      zone = 'left';
    } else {
      zone = 'right';
    }

    return TrajectorySystem.buildMissTrajectory(
      startScreenX,
      startScreenY,
      xOffset,
      zone,
      forceRatio,
      config,
      rotationSpeed,
      headPos,
    );
  }

  private static buildHitTrajectory(
    startX: number,
    startY: number,
    xOffset: number,
    headPos: HeadPos,
    forceRatio: number,
    config: TrajectoryConfig,
    rotationSpeed: number,
    hitType: 'perfect' | 'good' | 'just',
  ): Trajectory {
    const endX = headPos.x;
    const endY = headPos.y;
    const endZ = headPos.z;

    const peakX = (startX + endX) / 2 + xOffset * 0.5;
    const peakY = Math.min(startY, endY) - 360;
    const peakZ = -0.4;

    return {
      startX,
      startY,
      startZ: -1.0,
      peakX,
      peakY,
      peakZ,
      endX,
      endY,
      endZ,
      duration: config.flightDuration,
      willHit: true,
      hitType,
      missZone: null,
      forceRatio,
      lateralOffset: xOffset,
      rotationSpeed,
    };
  }

  private static buildMissTrajectory(
    startX: number,
    startY: number,
    xOffset: number,
    zone: MissZone,
    forceRatio: number,
    config: TrajectoryConfig,
    rotationSpeed: number,
    headPos: HeadPos,
  ): Trajectory {
    const zoneTargets: Record<MissZone, { z: number; x: number }> = {
      front: { z: -0.1, x: GAME_CONFIG.CHAR_CENTER_X + xOffset * 1.5 },
      back: { z: 0.7, x: GAME_CONFIG.CHAR_CENTER_X + xOffset * 1.5 },
      left: { z: 0, x: 120 },
      right: { z: 0, x: 600 },
    };

    const target = zoneTargets[zone];
    const floorY = getFloorY(target.z);
    const peakX = (startX + target.x) / 2;
    const peakY =
      zone === 'back'
        ? headPos.y - 620
        : Math.min(startY, floorY) - 360;
    const peakZ = (-1.0 + target.z) / 2;

    return {
      startX,
      startY,
      startZ: -1.0,
      peakX,
      peakY,
      peakZ,
      endX: target.x,
      endY: floorY,
      endZ: target.z,
      duration: config.flightDuration,
      willHit: false,
      hitType: null,
      missZone: zone,
      forceRatio,
      lateralOffset: xOffset,
      rotationSpeed,
    };
  }
}
